import { Box, Button, Typography, getContrastRatio } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import "@fontsource/rubik";
import CustomAlert from "../../pages/events/components/CustomAlert";
import useCustomTheme from "../../theme/useCustomTheme";
import { generateLighterContrastColor } from "../../utils/commonServices";

/**
 * Confirms removing one file from the open revision request.
 *
 * Deleting is final - the file is gone from the request and, if it was still
 * uploading, the upload is cancelled - so it gets the same popup the revision
 * log already uses for its own deletes, rather than something lighter.
 *
 * The filename is in the question because the list can hold several near
 * identical takes, and "this file" is no help when it does.
 */
const DeleteRevisionContentPopup = (props) => {
  const { open, file, isDeleting = false, onCancel, onConfirm } = props;
  const [theme] = useCustomTheme();

  // The same contrast-checked accent the rest of the revision screens use.
  const accent = theme.palette.primary.main === ('#00d744' || '#00D744')
    ? '#00d744'
    : getContrastRatio('#08231B', theme.palette.primary.main) > 4.5
      ? `${theme.palette.primary.main}`
      : generateLighterContrastColor(theme.palette.primary.main);

  const buttonSx = {
    fontSize: { xs: '9px', sm: '9px', md: '9px', lg: '11px', xl: '16px' },
    fontWeight: 600,
    width: { xs: '86px', sm: '86px', md: '86px', lg: '96px', xl: '139px' },
    paddingY: 0.4,
    minWidth: '110px !important',
    fontFamily: 'Rubik',
  };

  return (
    <CustomAlert
      open={open}
      handleClose={onCancel}
      data-testid='revision-content-delete-popup'
      width={{ xs: '400px', sm: '400px', md: '420px', lg: '440px', xl: '600px' }}
      height={{ xs: '240px', sm: '240px', md: '250px', lg: '250px', xl: '320px' }}
    >
      <Box p={5} className='flex col j-center' sx={{ height: '100%' }}>
        <Box mb={{ xs: 1, md: 2 }} mt={1}>
          <Typography
            variant='h4'
            sx={{ fontFamily: 'Rubik', color: 'black', fontSize: { xs: '13px', sm: '13px', md: '13px', lg: '13px', xl: '18px' } }}
          >
            Are you sure you want to delete this file?
          </Typography>

          {!!file?.filename && (
            <Typography
              data-testid='revision-content-delete-filename'
              sx={{ fontFamily: 'Rubik', fontWeight: 600, color: '#08231B', mt: 1, wordBreak: 'break-word', fontSize: { xs: '10px', md: '11px', lg: '12px', xl: '16px' } }}
            >
              {file.filename}
            </Typography>
          )}

          {/* Deleting a file mid-upload cancels it, which is worth saying before
              rather than after. */}
          {file?.status !== 'completed' && (
            <Typography
              data-testid='revision-content-delete-uploading-warning'
              sx={{ fontFamily: 'Rubik', fontStyle: 'italic', color: '#08231B', opacity: 0.75, mt: 0.6, fontSize: { xs: '9px', md: '10px', lg: '10px', xl: '13px' } }}
            >
              This file is still uploading. Deleting it cancels the upload.
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'end' }} gap={2} mt={{ xs: 2, md: 2.5, lg: 3 }}>
          <Button
            variant='outlined'
            color='button'
            onClick={onCancel}
            data-testid='revision-content-delete-cancel'
            style={{ borderRadius: '5px', color: accent, textTransform: 'none' }}
            sx={buttonSx}
          >
            Cancel
          </Button>

          <LoadingButton
            loading={isDeleting}
            loadingPosition='start'
            color='button'
            onClick={onConfirm}
            data-testid='revision-content-delete-confirm'
            style={{ borderRadius: '5px', backgroundColor: '#08231B', color: accent, textTransform: 'none' }}
            sx={buttonSx}
          >
            Continue
          </LoadingButton>
        </Box>
      </Box>
    </CustomAlert>
  )
}

export default DeleteRevisionContentPopup
